import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Button, Card, Form } from 'react-bootstrap';
import { useAudit } from './LegalContext';
import AuditWidget from './LegalWidget';
import { FeedbackMessage, AuditPageProps, ClientInfo } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset,
  faInfoCircle,
  faExclamationTriangle,
  faGavel,
  faComments,
  faArrowsRotate,
  faUserFriends,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import Avatar from 'components/base/Avatar';
import logoAuditCo from 'assets/img/icons/logo-lexco.png';
import { auditService11 } from './legal-service';

// Definir los escenarios de instrucciones
const INSTRUCTION_SCENARIOS = {
  OUTBOUND_COLLECTION: {
    label: 'Llamada saliente de cobro para venta consensuada',
    instructions: `Eres un asistente legal especializado en cobros. Tu objetivo es contactar al cliente para negociar un acuerdo de pago mediante una venta consensuada de la propiedad. Debes ser profesional, empático y buscar una solución mutuamente beneficiosa.
                  Puntos clave:
                  1. Presentarte como asistente legal de la empresa acreedora
                  2. Explicar claramente la situación de la deuda
                  3. Proponer la venta consensuada como alternativa beneficiosa
                  4. Detallar las ventajas de esta opción frente a un proceso judicial
                  5. Escuchar las preocupaciones del cliente y buscar soluciones
                  6. Documentar los acuerdos alcanzados`
  },
  INBOUND_DACION: {
    label: 'Llamada saliente para gestión de dación en pago',
    instructions: `Eres un asistente legal especializado en dación en pago. Tu función es asesorar al cliente sobre este proceso. Debes ser comprensivo, claro y proporcionar información legal precisa.
                  Puntos clave:
                  1. Escuchar atentamente la situación del cliente
                  2. Explicar qué es la dación en pago y requisitos legales
                  3. Informar sobre las implicaciones fiscales y legales
                  4. Detallar el procedimiento paso a paso
                  5. Advertir sobre consecuencias y alternativas disponibles
                  6. Ofrecer acompañamiento durante el proceso`
  },
  GENERIC_LEGAL: {
    label: 'Llamada genérica para consultar aspectos legales',
    instructions: `Eres un asistente legal versátil preparado para responder cualquier consulta legal. El cliente nos contactó y acordamos hablar hoy y responder a sus preguntas y aclararle dudas. Debes ser profesional, preciso y siempre citar las fuentes legales pertinentes según tu base de conocimientos.`
  }
};

const AuditPage: React.FC<AuditPageProps> = ({
  title,
  agentId,
  agentName,
  agentImage,
  onError,
  onClose,
  clientName,
  clientFirstSurname,
  clientSecondSurname,
  clientPhone,
  clientEmail,
  firstMessage,
  prompt,
  voice
}) => {
  const {
    selectedClient,
    setSelectedClient,
    conversationStage,
    setConversationStage,
    systemMessages,
    addSystemMessage,
    agentInstructions,
    updateAgentInstructions,
    clearLegalInformation
  } = useAudit();

  // Estado para el escenario seleccionado
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  // State to manage filtered system messages
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Lista de clientes disponibles
  const [availableClients, setAvailableClients] = useState<ClientInfo[]>([]);

  // Estado local para el cliente seleccionado
  const [currentClientInfo, setCurrentClientInfo] = useState({
    name: clientName,
    firstSurname: clientFirstSurname,
    secondSurname: clientSecondSurname,
    phone: clientPhone,
    email: clientEmail
  });

  // Función para obtener el nombre completo del cliente
  const getClientFullName = () => {
    return `${currentClientInfo.name} ${currentClientInfo.firstSurname} ${currentClientInfo.secondSurname}`.trim();
  };

  // Cargar los clientes disponibles al iniciar
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Cargar lista de clientes
        const clients = auditService11.getClients();
        setAvailableClients(clients);

        // Buscar el cliente que coincida con las props o usar el primero
        const initialClient =
          clients.find(
            c => c.name === clientName && c.firstSurname === clientFirstSurname
          ) || clients[0];

        if (initialClient) {
          // Establecer el cliente seleccionado en el estado local
          setCurrentClientInfo({
            name: initialClient.name,
            firstSurname: initialClient.firstSurname,
            secondSurname: initialClient.secondSurname,
            phone: initialClient.phoneNumber,
            email: initialClient.email
          });

          // Establecer el cliente en el contexto
          setSelectedClient(initialClient);

          // Registrar la acción
          addSystemMessage(
            'info',
            `Cliente inicial: ${auditService11.getClientFullName(
              initialClient
            )}`
          );
        }
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
        addSystemMessage('error', 'Error cargando la lista de clientes');
      }
    };

    // Ejecutar la función de carga inicial
    loadInitialData();
  }, []);

  // Convert system messages to feedback messages
  useEffect(() => {
    const newFeedbackMessages = systemMessages.map(msg => ({
      id: msg.id,
      timestamp: msg.timestamp,
      type: msg.type as 'info' | 'error' | 'legal',
      message: msg.message,
      details: msg.details || '',
      success: msg.type !== 'error'
    }));

    setFeedbackMessages(newFeedbackMessages);
  }, [systemMessages]);

  // Manejador para cambiar de cliente
  const handleClientChange = (clientId: string) => {
    const newClient = availableClients.find(c => c.id === clientId);
    if (!newClient) return;

    // Actualizar el cliente seleccionado en el contexto
    setSelectedClient(newClient);

    // Actualizar la información local
    setCurrentClientInfo({
      name: newClient.name,
      firstSurname: newClient.firstSurname,
      secondSurname: newClient.secondSurname,
      phone: newClient.phoneNumber,
      email: newClient.email
    });

    // Registrar el cambio
    addSystemMessage(
      'info',
      `Cliente cambiado a: ${auditService11.getClientFullName(newClient)}`
    );
  };

  // Manejador para cambiar de escenario
  const handleScenarioChange = (scenarioKey: string) => {
    setSelectedScenario(scenarioKey);

    if (
      scenarioKey &&
      INSTRUCTION_SCENARIOS[scenarioKey as keyof typeof INSTRUCTION_SCENARIOS]
    ) {
      const scenario =
        INSTRUCTION_SCENARIOS[
          scenarioKey as keyof typeof INSTRUCTION_SCENARIOS
        ];
      updateAgentInstructions(scenario.instructions);
      addSystemMessage('info', `Escenario seleccionado: ${scenario.label}`);
    }
  };

  // Function to reset simulation
  const handleReset = () => {
    setConversationStage('ready');
    clearLegalInformation();
    setSelectedScenario('');
    updateAgentInstructions('');
    addSystemMessage('info', 'La simulación ha sido reiniciada');
  };

  // Render current status panel
  const renderStatusPanel = () => {
    const getStatusIcon = () => {
      switch (conversationStage) {
        case 'ready':
          return faHeadset;
        case 'in_conversation':
          return faGavel;
        case 'finished':
          return faComments;
        default:
          return faHeadset;
      }
    };

    const getStatusText = () => {
      switch (conversationStage) {
        case 'ready':
          return 'Listo para conversar';
        case 'in_conversation':
          return 'En conversación';
        case 'finished':
          return 'Conversación finalizada';
        default:
          return 'Estado desconocido';
      }
    };

    return (
      <div className="bg-body d-flex align-items-center border rounded mb-3 px-2 py-2">
        <div className="mx-3">
          <Avatar
            size="4xl"
            src={agentImage}
            placeholder={true}
            variant="image"
            rounded="circle"
            imageClassName="avatar-image-top shadow-sm border bg-body-emphasis"
          />
        </div>
        <div className="mx-2 me-14 text-center align-items-center d-flex flex-column flex-grow-1">
          <FontAwesomeIcon icon={getStatusIcon()} className="me-2 fs-6" />
          <div>
            <strong>{getStatusText()}</strong>
          </div>
        </div>
      </div>
    );
  };

  // Render agent instructions panel
  const renderAgentInstructionsPanel = () => {
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faEdit} className="me-2 text-primary" />
            Instrucciones para el Agente
          </h5>
        </div>

        <div className="p-3 pt-2 border rounded bg-body">
          {/* Selector de escenarios */}
          <div className="mb-1">
            <div className="d-flex align-items-center mb-0 fs-9 px-2">
              <strong>Escenarios predefinidos:</strong>
            </div>
            <Form.Select
              value={selectedScenario}
              onChange={e => handleScenarioChange(e.target.value)}
            >
              <option value="">Seleccionar un escenario...</option>
              {Object.entries(INSTRUCTION_SCENARIOS).map(([key, scenario]) => (
                <option key={key} value={key}>
                  {scenario.label}
                </option>
              ))}
            </Form.Select>
          </div>

          <Form.Group className="mb-20">
            <Form.Control
              as="textarea"
              rows={8}
              placeholder="Escriba las instrucciones específicas para el agente legal..."
              value={agentInstructions.instructions}
              onChange={e => updateAgentInstructions(e.target.value)}
              className="fs-9 fw-normal scrollbar"
            />
          </Form.Group>
        </div>
      </div>
    );
  };

  // Render client selector
  const renderClientSelector = () => {
    return (
      <div className="mb-3 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faUserFriends}
              className="me-2 text-primary"
            />
            Seleccionar Cliente
          </h5>
        </div>
        <Form.Select
          value={selectedClient?.id || ''}
          onChange={e => handleClientChange(e.target.value)}
          className="bg-body"
        >
          {availableClients.map(client => (
            <option key={client.id} value={client.id}>
              {auditService11.getClientFullName(client)}
            </option>
          ))}
        </Form.Select>
      </div>
    );
  };

  // Render system messages panel
  const renderFeedbackPanel = () => {
    return (
      <>
        <h6 className="pb-0 pt-2 mb-2">Mensajes del Sistema</h6>
        <div
          className="p-2 border rounded scrollbar bg-body-emphasis"
          style={{
            height: '218px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {feedbackMessages.length === 0 ? (
            <p className="text-center text-muted fs-9 pt-4">
              No hay mensajes del sistema
            </p>
          ) : (
            feedbackMessages.map(msg => {
              const getIcon = () => {
                switch (msg.type) {
                  case 'info':
                    return faInfoCircle;
                  case 'legal':
                    return faGavel;
                  case 'error':
                    return faExclamationTriangle;
                  default:
                    return faInfoCircle;
                }
              };

              const getColor = () => {
                if (msg.type === 'error') return 'text-danger';
                if (msg.type === 'legal') return 'text-primary';
                return 'text-body-quaternary';
              };

              return (
                <div
                  key={msg.id}
                  className="alert bg-body p-2 mb-2 border rounded"
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={getIcon()}
                      className={`me-2 ${getColor()}`}
                    />
                    <div className="d-flex flex-column flex-grow-1">
                      <small className="text-muted fs-10">
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </small>
                      <div className="fs-9 lh-1">{msg.message}</div>
                      {msg.details && (
                        <small className="mt-1 text-muted fs-10 lh-1">
                          {msg.details}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </>
    );
  };

  return (
    <div className="bg-body mt-n3">
      <Container fluid className="px-0">
        {/* Page Header & Breadcrumb */}
        <Row className="mb-3">
          <Col>
            <nav aria-label="breadcrumb" className="p-0 pb-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      if (onClose) {
                        onClose();
                      } else {
                        // Fallback if no close function
                        window.history.back();
                      }
                    }}
                  >
                    POCs
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Agente Legal Versátil
                </li>
              </ol>
            </nav>
            <div className="d-flex align-items-center mb-3">
              <h2 className="mb-0">{title || 'Agente Legal Versátil'}</h2>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          {/* Left column: Instructions */}
          <Col md={6}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-2">
                  <img
                    src={logoAuditCo}
                    alt={`Logo`}
                    width={130}
                    className="mb-3"
                  />
                </div>

                {renderStatusPanel()}

                {renderClientSelector()}

                {renderAgentInstructionsPanel()}

                <div className="mb-0 bg-body rounded border p-2">
                  <div className="mb-0 bg-body p-2">
                    <h6 className="pb-0">Información:</h6>
                    <ul className="mb-0 ps-3">
                      <li className="mb-1 small lh-sm">
                        Puede seleccionar un escenario predefinido o crear
                        instrucciones personalizadas.
                      </li>
                      <li className="mb-1 small lh-sm">
                        Si no incluye nada en instrucciones, puede comprobar el
                        conocimiento legal del agente haciéndole cualquier
                        pregunta
                      </li>
                    </ul>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Center column: Conversation widget */}
          <Col md={6}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="mb-3 border-bottom pb-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">Conversación con {agentName}</h5>
                    <p className="text-muted small mb-0">
                      Pulse el botón para iniciar o finalizar la conversación
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleReset}
                    title="Reiniciar simulación"
                    className="bg-body-quaternary pt-1 pb-0"
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                  </Button>
                </div>

                <div className="flex-grow-1 mb-3">
                  <AuditWidget
                    clientName={getClientFullName()}
                    clientCompany="" // No longer using company
                    agentId={agentId}
                    agentName={agentName}
                    onError={onError}
                    onFeedback={message =>
                      addSystemMessage(
                        message.type,
                        message.message,
                        message.details
                      )
                    }
                    firstMessage={firstMessage}
                    prompt={prompt}
                    voice={voice}
                  />
                </div>

                {renderFeedbackPanel()}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuditPage;
